/**
 * Left pane — the conversation list. Newest first (the backend already orders by
 * `updated_at desc`), a new-conversation button, active highlight, delete on hover.
 *
 * Collapsible: when `collapsed`, renders as a narrow icon strip (matching the panel's
 * `collapsedSize`) with just the expand affordance and "new conversation" — the full list
 * reappears once expanded.
 */

import { ChevronsLeft, ChevronsRight, Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { type ConversationSummary, useConversations, useDeleteConversation } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ConversationListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  creating: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Called once a delete succeeds for the conversation that was on screen — the chat page uses
   *  this to fall back to the empty "no conversation selected" state instead of being left
   *  pointed at an id that no longer exists (which otherwise surfaces as a load error, not the
   *  intended empty state). */
  onDeleteActive: () => void;
}

function ConversationRow({
  conversation,
  active,
  onSelect,
  onDeleteActive,
}: {
  conversation: ConversationSummary;
  active: boolean;
  onSelect: () => void;
  onDeleteActive: () => void;
}) {
  const deleteMutation = useDeleteConversation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className={cn(
        "group flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm",
        active ? "bg-primary/10 text-primary" : "hover:bg-primary/5 text-foreground",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{conversation.title || "Untitled"}</span>
      <button
        type="button"
        aria-label="Delete conversation"
        className="text-muted-foreground hover:text-destructive shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        disabled={deleteMutation.isPending}
        onClick={(e) => {
          e.stopPropagation();
          deleteMutation.mutate(
            { conversationId: conversation.id },
            { onSuccess: () => active && onDeleteActive() },
          );
        }}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function ConversationList({
  selectedId,
  onSelect,
  onNew,
  creating,
  collapsed,
  onToggleCollapsed,
  onDeleteActive,
}: ConversationListProps) {
  const conversationsQuery = useConversations();

  if (collapsed) {
    return (
      <div className="flex h-full flex-col items-center gap-2 py-3">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={onToggleCollapsed}
          aria-label="Expand conversation list"
          title="Expand conversation list"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={onNew}
          disabled={creating}
          aria-label="New conversation"
          title="New conversation"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The pane is deliberately narrow, so the header has to survive being squeezed: the
          label truncates and the two controls never shrink out of reach. */}
      <div className="border-border flex shrink-0 items-center justify-between gap-1 border-b p-2">
        <div className="flex min-w-0 items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            onClick={onToggleCollapsed}
            aria-label="Collapse conversation list"
            title="Collapse conversation list"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <span className="font-heading text-foreground truncate text-sm font-semibold">
            Conversations
          </span>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={onNew}
          disabled={creating}
          aria-label="New conversation"
          title="New conversation"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1.5">
        {conversationsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted h-4 w-4 animate-spin" />
          </div>
        ) : conversationsQuery.error ? (
          <p className="text-muted-foreground p-2 text-xs">
            Couldn&apos;t load your conversations.
          </p>
        ) : conversationsQuery.data?.length ? (
          conversationsQuery.data.map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              active={c.id === selectedId}
              onSelect={() => onSelect(c.id)}
              onDeleteActive={onDeleteActive}
            />
          ))
        ) : (
          <p className="text-muted-foreground p-2 text-xs">
            No conversations yet — start one to ask the agent about your SKU portfolio.
          </p>
        )}
      </div>
    </div>
  );
}
